import { GoogleSupportedLanguage } from 'dashboard/lib/constants/locales';
import { ExtractionRequest, ExtractionResponse } from 'dashboard/models/document';
import { Translations } from 'dashboard/redux//slices/translations';
import {
  ExtractionFilterValue,
  FocusedExtraction,
} from 'dashboard/redux/reducers/document-viewer-document-generic';
import { DocumentSearchMatch } from 'dashboard/views/DashboardDocumentViewerView/DocumentViewerSearch';
import Action from 'lib/interfaces/Action';

interface SetExtractionsPayload {
  extractions: ExtractionResponse[];
  extractionConfigs: ExtractionRequest[];
}

interface SetFocusedExtractionPayload {
  focusedExtractions: FocusedExtraction[];
}

interface SetHoveredExtractionPayload {
  hoveredExtractions: FocusedExtraction[];
}

interface SetExtractionFiltersPayload {
  extractionFilters: ExtractionFilterValue[];
}

interface SetInSearchModePayload {
  inSearchMode: boolean;
}

interface SetSearchHighlightsPayload {
  searchMatches: DocumentSearchMatch[];
}

interface SetSearchIndexPayload {
  searchIndex: number;
}

interface SetFileNumPagesPayload {
  fileIndex: number;
  numPages: number;
}

interface SetTranslationLoading {
  translationLoading: boolean;
}

interface SetTranslations {
  translations: Translations | null;
}

interface SetTranslationLanguage {
  translationLanguage: GoogleSupportedLanguage | null;
}

export type DocumentViewerGenericExtractionsActions =
  | Action<
      DocumentViewerGenericExtractionsActionType.ResetDocumentGenericState,
      Record<string, never>
    >
  | Action<DocumentViewerGenericExtractionsActionType.SetExtractions, SetExtractionsPayload>
  | Action<
      DocumentViewerGenericExtractionsActionType.SetFocusedExtraction,
      SetFocusedExtractionPayload
    >
  | Action<
      DocumentViewerGenericExtractionsActionType.RemoveFocusedExtraction,
      Record<string, never>
    >
  | Action<
      DocumentViewerGenericExtractionsActionType.SetHoveredExtraction,
      SetHoveredExtractionPayload
    >
  | Action<
      DocumentViewerGenericExtractionsActionType.RemoveHoveredExtraction,
      Record<string, never>
    >
  | Action<
      DocumentViewerGenericExtractionsActionType.SetExtractionFilters,
      SetExtractionFiltersPayload
    >
  | Action<DocumentViewerGenericExtractionsActionType.SetInSearchMode, SetInSearchModePayload>
  | Action<
      DocumentViewerGenericExtractionsActionType.SetSearchHighlights,
      SetSearchHighlightsPayload
    >
  | Action<DocumentViewerGenericExtractionsActionType.SetSearchIndex, SetSearchIndexPayload>
  | Action<DocumentViewerGenericExtractionsActionType.SetFileNumPages, SetFileNumPagesPayload>
  | Action<DocumentViewerGenericExtractionsActionType.SetTranslationLoading, SetTranslationLoading>
  | Action<DocumentViewerGenericExtractionsActionType.SetTranslations, SetTranslations>
  | Action<
      DocumentViewerGenericExtractionsActionType.SetTranslationLanguage,
      SetTranslationLanguage
    >;

export enum DocumentViewerGenericExtractionsActionType {
  ResetDocumentGenericState = 'document-viewer-generic-extractions/RESET_DOCUMENT_GENERIC_STATE',
  SetExtractions = 'document-viewer-generic-extractions/SET_EXTRACTIONS',
  SetFocusedExtraction = 'document-viewer-generic-extractions/SET_FOCUSED_EXTRACTION',
  RemoveFocusedExtraction = 'document-viewer-generic-extractions/REMOVE_FOCUSED_EXTRACTION',
  SetHoveredExtraction = 'document-viewer-generic-extractions/SET_HOVERED_EXTRACTION',
  RemoveHoveredExtraction = 'document-viewer-generic-extractions/REMOVE_HOVERED_EXTRACTION',
  SetExtractionFilters = 'document-viewer-generic-extractions/SET_EXTRACTION_FILTERS',
  SetInSearchMode = 'document-viewer-generic-extractions/SET_IN_SEARCH_MODE',
  SetSearchHighlights = 'document-viewer-generic-extractions/SET_SEARCH_HIGHLIGHTS',
  SetSearchIndex = 'document-viewer-generic-extractions/SET_SEARCH_INDEX',
  SetFileNumPages = 'document-viewer-generic-extractions/SET_FILE_NUM_PAGES',
  SetTranslationLoading = 'document-viewer-generic-extractions/SET_TRANSLATION_LOADING',
  SetTranslations = 'document-viewer-generic-extractions/SET_TRANSLATIONS',
  SetTranslationLanguage = 'document-viewer-generic-extractions/SET_TRANSLATION_LANGUAGE',
}

export function resetDocumentGenericState() {
  return {
    type: DocumentViewerGenericExtractionsActionType.ResetDocumentGenericState,
  };
}
export function setExtractions(
  extractions: ExtractionResponse[],
  extractionConfigs: ExtractionRequest[],
) {
  return {
    payload: {
      extractions,
      extractionConfigs,
    },
    type: DocumentViewerGenericExtractionsActionType.SetExtractions,
  };
}
export function setFocusedExtraction(focusedExtractions: FocusedExtraction[]) {
  return {
    payload: {
      focusedExtractions,
    },
    type: DocumentViewerGenericExtractionsActionType.SetFocusedExtraction,
  };
}
export function removeFocusedExtraction() {
  return {
    type: DocumentViewerGenericExtractionsActionType.RemoveFocusedExtraction,
  };
}
export function setHoveredExtraction(hoveredExtractions: FocusedExtraction[]) {
  return {
    payload: {
      hoveredExtractions,
    },
    type: DocumentViewerGenericExtractionsActionType.SetHoveredExtraction,
  };
}
export function removeHoveredExtraction() {
  return {
    type: DocumentViewerGenericExtractionsActionType.RemoveHoveredExtraction,
  };
}
export function setExtractionFilters(extractionFilters: ExtractionFilterValue[]) {
  return {
    payload: {
      extractionFilters,
    },
    type: DocumentViewerGenericExtractionsActionType.SetExtractionFilters,
  };
}
export function setInSearchMode(inSearchMode: boolean) {
  return {
    payload: {
      inSearchMode,
    },
    type: DocumentViewerGenericExtractionsActionType.SetInSearchMode,
  };
}
export function setSearchHighlights(searchMatches: DocumentSearchMatch[]) {
  return {
    payload: {
      searchMatches,
    },
    type: DocumentViewerGenericExtractionsActionType.SetSearchHighlights,
  };
}
export function setSearchIndex(searchIndex: number) {
  return {
    payload: {
      searchIndex,
    },
    type: DocumentViewerGenericExtractionsActionType.SetSearchIndex,
  };
}
export function setFileNumPages(fileIndex: number, numPages: number) {
  return {
    payload: {
      fileIndex,
      numPages,
    },
    type: DocumentViewerGenericExtractionsActionType.SetFileNumPages,
  };
}
export function setTranslationLoading(translationLoading: boolean) {
  return {
    payload: {
      translationLoading,
    },
    type: DocumentViewerGenericExtractionsActionType.SetTranslationLoading,
  };
}
export function setTranslations(translations: Translations | null) {
  return {
    payload: {
      translations,
    },
    type: DocumentViewerGenericExtractionsActionType.SetTranslations,
  };
}
export function setTranslationLanguage(translationLanguage: GoogleSupportedLanguage | null) {
  return {
    payload: {
      translationLanguage,
    },
    type: DocumentViewerGenericExtractionsActionType.SetTranslationLanguage,
  };
}
